import type { ReactNode } from 'react';

/**
 * Fields side by side, aligned (D328).
 *
 * A row of fields whose labels differ in length must not stagger: the longer
 * label would push its input below its neighbours'. `FieldGrid` lays fields out
 * in a grid marked `.aligned-fields` (styles.css), where every label reserves
 * the same two-line area and sits at its foot, so every control in a row starts
 * on the same line. The fields themselves are unchanged — this arranges them.
 *
 * - `2`: one column on a phone, two from 640 px — a panel's ordinary fields.
 * - `row`: sized by its CONTAINER, not the window, so a side column can never
 *   squeeze it — one column below 28 rem, two from 28 rem, and from 42 rem every
 *   field in ONE row of equal columns, however many there are (a determination
 *   card's three or four readings). Needs an `@container` ancestor, which the
 *   component and `DeterminationCard` provide. Its text fields are aligned
 *   EXACTLY, by subgrid (`.aligned-rows`), whatever their labels' length.
 */
export type FieldGridColumns = 2 | 'row';

export function fieldGridClass(columns: FieldGridColumns = 2): string {
  return columns === 'row'
    ? 'aligned-fields aligned-rows grid grid-cols-1 gap-x-stack gap-y-3 @md:grid-cols-2 @2xl:grid-flow-col @2xl:auto-cols-fr @2xl:grid-cols-none @2xl:grid-rows-[auto_auto]'
    : 'aligned-fields grid grid-cols-1 gap-x-stack gap-y-stack sm:grid-cols-2';
}

export function FieldGrid({
  columns = 2,
  children,
  className,
  testId,
}: {
  readonly columns?: FieldGridColumns;
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}) {
  const grid = (
    <div
      className={`${fieldGridClass(columns)}${className ? ` ${className}` : ''}`}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {children}
    </div>
  );
  return columns === 'row' ? <div className="@container">{grid}</div> : grid;
}
